import { createContext, useContext } from 'react'
import type {
  EnhanceController,
  EnhanceRequestOptions,
} from './useEnhanceImage'

/**
 * The editor's one shared Enhance flow, handed to every entry point that can
 * open it: the Case tab's grid, the low-resolution warning on a canvas item,
 * and a canvas photo's "Revert changes" and "Show enhancement". Sharing one controller means one modal instance and one in-flight
 * guard, so two paid generations can never run at once whichever way the
 * user started.
 */
export type SharedEnhance = {
  /** The state machine and its actions, owned by the provider. */
  enhance: EnhanceController
  /**
   * Open the Enhance flow for a case image by id. Loads the case's images,
   * variants and pricing first so the flow works before the Case tab has
   * ever been opened. `options.targetSize` is the size a placed photo needs;
   * the low-resolution warning on a canvas item sends it.
   */
  enhanceCaseImage: (
    caseImageId: string,
    options?: EnhanceRequestOptions,
  ) => Promise<void>
  /**
   * Revert a case photo's accepted enhancement from a canvas item. Loads the
   * case's images and variants first, then opens the same revert
   * confirmation the Case Images grid opens, in the editor's modal, for the
   * newest accepted enhancement. When the reload shows the photo no longer
   * shows an enhancement, nothing is saved and the canvas moves to what the
   * server shows.
   */
  revertCaseImage: (caseImageId: string) => Promise<void>
  /**
   * Undo a revert from a canvas item: show the photo's accepted enhancement
   * again, for everyone and at no charge, after the same fresh load. When
   * the reload shows the photo is no longer reverted, nothing is saved and
   * the canvas moves to what the server shows.
   */
  showCaseImageEnhancement: (caseImageId: string) => Promise<void>
  /**
   * Fetch the per-use price if it is not loaded yet. Tooltips call this when
   * they appear so the price can be shown without opening the Case tab.
   */
  ensurePricing: () => Promise<void>
  /**
   * The editor's complete refresh after the photo an image shows changed on
   * the server (an accepted enhancement, a revert to the original, or undoing
   * one): reload the case's images, refill the template slots, point the
   * canvas items showing that photo at the file the server now prefers and
   * save them to the editor pages. A host inside the editor that runs its own
   * flow (the case details modal's takeover) calls it after each of those, so
   * the canvas follows there too.
   */
  refreshShownImage: (caseImageId: string) => Promise<void>
}

/** Null outside the editor, where each host runs its own Enhance flow. */
export const EnhanceContext = createContext<SharedEnhance | null>(null)

/**
 * The editor's shared Enhance flow, or null when the component is rendered
 * somewhere that has no provider (the case details modal, tests).
 */
export const useSharedEnhance = (): SharedEnhance | null =>
  useContext(EnhanceContext)
